// ── Contextual help tooltip definitions ────────────────────────────────────────
// Each key maps to a concise tooltip shown next to UI elements.
// Grouped by page/feature area.

export const TOOLTIPS: Record<string, string> = {
  // ── Reservations ────────────────────────────────────────────────────────────
  'reservation.statusFilter':
    'Click to filter by status. Ctrl+Click to select multiple statuses at once.',
  'reservation.actionableUnits':
    'Shows how many unfulfilled units can be built from allocated stock. If less than needed, some components are awaiting delivery.',
  'reservation.stockWarning':
    'Some components are not yet in stock but are on order. Assembly cannot start until stock arrives.',
  'reservation.fulfillment':
    'Shows how many assembly orders have been Posted (approved) vs total ordered.',
  'reservation.componentFilter':
    'Filtered by a specific component SKU. Click X to clear and see all reservations.',
  'reservation.status':
    'Pending = no active work yet. In Progress = at least one assembly order is being built. Partial = some units fulfilled but no active builds. Completed = all units fulfilled. Cancelled = reservation cancelled.',
  'reservation.tableColumns':
    'Toggle component category columns visible in the table.',

  // ── Reservation Detail ──────────────────────────────────────────────────────
  'reservationDetail.buildSku':
    'Search by SKU (e.g. AOI-1A-G4) or build name. Auto-fills components when selected. Type without hyphens for auto-formatting.',
  'reservationDetail.qtyPerBuild':
    'How many of this component per system. Multiplied by reservation quantity for total allocation.',
  'reservationDetail.fifo':
    'Stock is allocated FIFO: first reservation gets first access. New stock is auto-redistributed across active reservations.',
  'reservationDetail.stockShortage':
    'Components listed are on order but not yet available. You can create the reservation, but assembly will wait for stock.',
  'reservationDetail.expectedQty':
    'Stock on supplier order, not yet received. Once arrived, auto-allocated to this reservation in FIFO order.',
  'reservationDetail.blueprintSync':
    'If you swap a component for an alternative, it\'s automatically added to the build blueprint and marked as active.',

  // ── Assembly Orders ─────────────────────────────────────────────────────────
  'assemblyOrders.statusFlow':
    'Orders progress: Pending → Building → Software → Testing → Awaiting Review → Posted. Each stage moves forward only.',
  'assemblyOrders.autoProgress':
    'When all items in a checklist section are checked, the order automatically advances to the next status.',
  'assemblyOrders.awaitingReview':
    'Technician finished all steps. Manager must review stage durations and approve before the order is Posted.',
  'assemblyOrders.posted':
    'Approved and completed. Counts toward reservation fulfillment. Cannot be edited after posting.',
  'assemblyOrders.stageDurations':
    'Actual time per stage. For unmanaged technicians, only counts days they signed in.',
  'assemblyOrders.techAssignment':
    'Assigned technician only sees their orders. Managers see their managed technicians\' orders.',
  'assemblyOrders.toNumber':
    'Internal Technical Order reference number for tracking.',

  // ── Checklist ───────────────────────────────────────────────────────────────
  'checklist.sections':
    'Each section has a trigger status. Checking all items auto-advances: Assembly→Software, Software→Testing, Final Touches→Awaiting Review.',
  'checklist.multiUnit':
    'Multi-unit orders track progress per unit. Each unit\'s checklist must be completed independently.',
  'checklist.weights':
    'Progress bar segments: Assembly/Software = 35% each, Benchmarking = 20%, Finishing + Final Touches = 10%.',

  // ── Components ──────────────────────────────────────────────────────────────
  'components.bcImport':
    'Import from Business Central. Updates part numbers, descriptions, pricing, and stock levels. For each category: Items > filter by Item Category Code > Edit in Excel > save as .xlsx.',
  'components.buildMatch':
    'Shows which build SKUs use this component. Based on comma-separated build tokens in the build_sku field.',
  'components.reserved':
    'Stock allocated to active reservations via FIFO. Not available for other reservations until released.',
  'components.expected':
    'Stock on supplier order, not yet received. Auto-allocated to waiting reservations when it arrives.',
  'components.onOrder':
    'Total quantity ordered from supplier. Adjusting triggers FIFO reallocation across active reservations.',
  'components.status':
    'Active = normal use. Inactive = cannot be added to new builds. EOL = still usable but should not be reordered.',

  // ── Component Detail ────────────────────────────────────────────────────────
  'componentDetail.stockAdjust':
    'Adjusting stock triggers FIFO reallocation across all active reservations using this component.',
  'componentDetail.specs':
    'Technical specs for compatibility checking and configurator filtering. Can be auto-filled via AI for known components.',
  'componentDetail.skuRename':
    'Super admin only. Renaming cascades to all builds, reservations, and historical orders with an audit trail.',
  'componentDetail.currentPrice':
    'Cost ex-VAT. Used to calculate build costs and RRP. Visible to admin/manager roles only.',
  'componentDetail.autoFill':
    'Uses Groq AI to auto-fill specifications from part number and description. Review before saving.',

  // ── Builds ──────────────────────────────────────────────────────────────────
  'build.bom':
    'Bill of Materials: all components with quantity per unit and cost. Active config checkboxes control which components count toward pricing.',
  'build.activeConfig':
    'Tick components to include in the active configuration. Only ticked components count toward build cost and appear in new reservations.',
  'build.cost':
    'Calculated from active component costs × quantities. Does not include labor or margin.',
  'build.rrp':
    'Recommended Retail Price. If empty, auto-calculated as: (cost × 1.25 × 1.18) rounded to nearest .99.',
  'build.eps':
    'Extended Product Support plans. Priced as: (RRP × markup) ÷ months. 12mo = 18%, 24mo = 30%, 36mo = 42%.',
  'build.status':
    'Active = available for new reservations. Inactive/Discontinued = no new orders, but historical data remains.',

  // ── Configurator ────────────────────────────────────────────────────────────
  'configurator.slots':
    'Required slots (marked *) must have a component before saving. Order: CPU → Cooler → RAM → MB → Storage → GPU → PSU → OS → Case → Case Fans → Accessories.',
  'configurator.specFilters':
    'Filter components by technical specs (e.g. socket, TDP, capacity). Combines with text search.',
  'configurator.availability':
    'Shows current on-hand stock. Zero-stock components may still be searchable if on order.',

  // ── Compare ─────────────────────────────────────────────────────────────────
  'compare.multiSelect':
    'Compare up to 4 build configurations side-by-side: specs, costs, RRP, and EPS plans.',
  'compare.printOptions':
    'Choose what to include: SKU codes, status, and pricing. Only authorized roles see pricing.',

  // ── Stock I/O ───────────────────────────────────────────────────────────────
  'stockio.duration':
    'Working days from order creation to completion. Weekends excluded. For unmanaged techs, only counts sign-in days.',
  'stockio.components':
    'Expand to see full BOM with unit costs and line totals for costing verification.',

  // ── Print Labels ────────────────────────────────────────────────────────────
  'printLabels.printer':
    'Your label printer is assigned by admin. Zebra = network (direct print). Brother = QZ Tray (requires app running).',
  'printLabels.queue':
    'Queue multiple labels before printing. Adjust copies per label. All sent in one batch when you press Print.',
  'printLabels.skuLabel':
    'Prints build SKU with barcode. For identifying units in stock.',
  'printLabels.snLabel':
    'Prints serial number with barcode. Apply to each finished unit for traceability.',

  // ── Admin ───────────────────────────────────────────────────────────────────
  'admin.role':
    'super_admin = full access + user management. admin = manage orders/builds. manager = oversee team. technician = build/test only.',
  'admin.managedBy':
    'Assigns a manager to this user. Managers see only their team\'s orders in dashboards.',
  'admin.printerAssignment':
    'Assign label and certificate printers. Click "Discover Printers" to find installed printers via QZ Tray.',
  'admin.isActive':
    'Inactive users cannot log in. Historical orders and data remain intact.',
  'admin.tooltips':
    'When enabled, contextual help icons appear throughout the app to guide users on features and workflow.',

  // ── Production ──────────────────────────────────────────────────────────────
  'production.queue':
    'Reservations waiting for assembly orders to be created. Create an order to move items into the pipeline.',
  'production.pipeline':
    'Active assembly orders in various stages. Track progress from Building through to Posted.',
};
